
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { MessageChat } from '@/store/chat/types';
import PlayerStore from '@/store/player';
import ClanStore from '@/store/clan';
import { ClanInfo, ClanPosition } from '@/store/clan/types';
import { AccessLevel, Player } from '@/store/player/types';
import { Settings } from '@/constants/settings';
import ListenerManager from '@/managers/listener-manager';
import MessageOptions from '@/components/MessageOptions.vue';

@Component({
  components: { MessageOptions },
})
export default class PcSideChatMessage extends Vue {
  @Prop({ required: true }) private message!: MessageChat;
  @Prop({ required: true }) private roomId!: string;
  @Prop({ default: 10 }) private accessLevel!: number;
  @Prop({ default: '' }) private sentTime!: string;

  public readonly smileyPrefix: string = Settings.SMILEY_MESSAGE_PREFIX;
  private readonly forwardedMessagePrefix: string = Settings.FORWARDED_MESSAGE_PREFIX;

  // MessageOptions expects these on parent:
  get messageText() {
    return this.baseMessData.text;
  }

  get isSmiley() {
    // Smiley message is a standalone message with prefix at the start.
    // Forwarded messages may contain the prefix inside the payload (quoted sticker),
    // but they should NOT be treated as a standalone smiley message.
    return this.rawMessageText.startsWith(this.smileyPrefix);
  }

  get isSmileyClass() {
    return this.isSmiley ? 'isSmiley' : '';
  }

  get isMine() {
    const player = PlayerStore.getPlayer();
    return player?.id === this.message.authorId;
  }

  get isMineClass() {
    return this.isMine ? 'isMine' : '';
  }

  get isModerator() {
    // same logic as ChatMessage.vue
    if (this.roomId === Settings.MAIN_CHAT_ROOM) {
      const accessLevel = PlayerStore.getPlayer()?.accessLevel;
      if (accessLevel === undefined) {
        return false;
      }
      return accessLevel !== AccessLevel.TRIAL && accessLevel !== AccessLevel.PLAYER;
    } else if (this.roomId === Settings.CLAN_CHAT_ROOM) {
      const ci: ClanInfo | undefined = ClanStore.getClanInfo();
      if (ci) {
        const clanPosition: ClanPosition = ci.me.clanPosition;
        return (
          clanPosition === ClanPosition.LEADER ||
          clanPosition === ClanPosition.DEPUTY_LEADER ||
          clanPosition === ClanPosition.SUBLEADER ||
          clanPosition === ClanPosition.REPRESENTATIVE
        );
      }
    }
    return false;
  }

  get messageClass() {
    const base = this.isMine ? 'sent' : 'received';
    const specials: string[] = [];
    if (this.message.support) specials.push('special-1');
    if (this.roomId === Settings.CLAN_CHAT_ROOM && this.isMine) specials.push('special-2');
    return [base, ...specials].join(' ');
  }

  get quoteTailStyle() {
    const isBottom = this.isMine && (this.message.support || this.roomId === Settings.CLAN_CHAT_ROOM);
    return isBottom
      ? { top: 'auto', bottom: '5px', right: '5px' }
      : { top: '5px', bottom: 'auto', right: '5px' };
  }

  get canQuote() {
    const p = PlayerStore.getPlayer();
    if (!p) return false;
    if (p.level < this.accessLevel) return false;
    // mirror "can write" behavior for clan chat
    if (this.roomId === Settings.CLAN_CHAT_ROOM && (!p.clanId || p.level < Settings.CLAN_MIN_LEVEL)) return false;
    return true;
  }

  get senderLabel() {
    if (this.isMine) return 'Вы:';
    if (this.message.support) return 'Система:';
    return `${this.message.authorName}:`;
  }

  get currentRule() {
    if (this.message.admin) return 'Adm';
    if (this.message.moder) return 'Mod';
    if (this.message.support) return 'Supp';
    return '';
  }

  get canOpenProfile() {
    return !this.isMine && !this.message.support && !!this.message.authorId;
  }

  get isForwarded() {
    const parts = this.rawMessageText.split(this.forwardedMessagePrefix);
    // expected: [name, quotedText, replyText]
    return parts.length >= 3;
  }

  get forwardedParts() {
    const parts = this.rawMessageText.split(this.forwardedMessagePrefix);
    // expected: [name, quotedText, replyText]
    return parts;
  }

  get forwardedName() {
    const parts = this.forwardedParts;
    return parts.length >= 3 ? String(parts[0] || '').trim() : '';
  }

  get forwardedText() {
    const parts = this.forwardedParts;
    return parts.length >= 3 ? String(parts[1] || '').trim() : '';
  }

  get displayText() {
    return this.displayedMessData.text;
  }

  get hasTopicLink() {
    return !!this.displayedMessData.topicName;
  }

  get topicId() {
    return this.displayedMessData.topicId;
  }

  get topicName() {
    return this.displayedMessData.topicName;
  }

  get messageSendingTime() {
    return this.sentTime || this.message?.sentAt || '';
  }

  get smileySrc() {
    const file = this.rawMessageText.slice(this.smileyPrefix.length).trim();
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require(`@/../public/assets/smiley/${file}`);
  }

  get forwardedIsSmiley() {
    if (!this.isForwarded) return false;
    return this.forwardedText.startsWith(this.smileyPrefix);
  }

  get forwardedSmileySrc() {
    const file = this.forwardedText.slice(this.smileyPrefix.length).trim();
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require(`@/../public/assets/smiley/${file}`);
  }

  get rawMessageText() {
    return this.message?.text ?? '';
  }

  get baseMessData() {
    // In "обычном" чате это парсится от полного текста сообщения
    return this.getMessData(this.rawMessageText);
  }

  get forwardedReplyRawText() {
    if (!this.isForwarded) return '';
    const parts = this.forwardedParts;
    return String(parts[parts.length - 1] || '').trim();
  }

  get displayedMessData() {
    // For forwarded messages we display the reply part, so parse links there.
    return this.isForwarded ? this.getMessData(this.forwardedReplyRawText) : this.baseMessData;
  }

  private quote() {
    this.$emit('quote', this.message);
  }

  private quoteFromTail() {
    if (!this.canQuote) return;
    this.quote();
  }

  private insertNicknameFromTail() {
    if (!this.canQuote) return;
    const name = (this.message?.authorName || '').trim();
    if (!name) return;
    this.$emit('insertNickname', name);
  }

  private openProfile(e: MouseEvent) {
    if (!this.canOpenProfile) return;
    e.stopPropagation();
    this.$root.$emit('openProfileCallback', this.message.authorId);
  }

  private deleteMessage() {
    this.$emit('delete', this.message.id);
  }

  private openTopicLink(e: MouseEvent) {
    e.stopPropagation();
    const id = this.topicId;
    if (!id) return;

    if (id.startsWith('player')) {
      const playerId = Number(id.replace('player', ''));
      if (!Number.isFinite(playerId)) return;
      this.$root.$emit('openProfileCallback', playerId);
      return;
    }

    const topicId = Number(id);
    if (!Number.isFinite(topicId)) return;
    ListenerManager.callOpenTopicCallback(topicId);
  }

  private openMessageOptions(e: MouseEvent) {
    // close any already open
    const openEls = document.getElementsByClassName('open');
    for (let i = openEls.length - 1; i >= 0; i--) {
      openEls[i].classList.remove('open');
    }

    // Use fixed-positioned menu to avoid being clipped by scroll containers
    // @ts-ignore
    this.$refs.messageOptions && this.$refs.messageOptions.setVisibleByClient(e.clientX, e.clientY);
  }

  // Same format as in ChatMessage.vue: "{{id||name}}"
  private getMessData(txt: string): MessData {
    const regex = /{{(.*)}}/;
    const res = txt.match(regex);
    if (res) {
      const str = res[0];
      if (str.includes('||')) {
        const arr = str.split('||');
        const id = String(arr[0] || '').replace('{{', '');
        const name = String(arr[1] || '').replace('}}', '');
        return new MessData(txt.replace(regex, ''), id, name);
      }
    }
    return new MessData(txt, undefined, undefined);
  }
}

class MessData {
  text!: string;
  topicId: string | undefined;
  topicName: string | undefined;

  constructor(text: string, topicId: string | undefined, topicName: string | undefined) {
    this.text = text;
    this.topicId = topicId;
    this.topicName = topicName;
  }
}
